/**
 * A station section laid out so that everything a student touches and
 * everything it changes are on the screen together.
 *
 * The sections were built top to bottom - explanation, charts, a 3D window,
 * a caption, the readouts, the buttons, and only then the knobs - so on an
 * ordinary browser window the slider being dragged and the picture it moves
 * were a scroll apart, and a student could watch one or the other. This puts
 * the pictures in a STAGE on the left and the run buttons, the knobs and the
 * readouts in a narrow BENCH on the right that stays in view while the stage
 * scrolls under it. Below about 780 px the bench drops under the stage and
 * takes the full width, which is the old order minus the distance.
 *
 *   <Workbench stage={<>pictures</>} bench={<>buttons, knobs, readouts</>} />
 *
 * Long prose belongs in <About>, folded away until somebody wants it: a
 * paragraph that pushes the experiment off the screen is read once and paid
 * for on every visit after.
 */

import type { ReactNode } from 'react'
import { Tooltip } from 'antd'
import type { Readout } from '../assistant/types'
import { STATUS } from '../ui/palette'
import { tOr } from '../i18n/i18n'

export function Workbench({ stage, bench, benchWidth = 300 }: { stage: ReactNode; bench: ReactNode; benchWidth?: number }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* 999 : 1 - in a row the stage takes every spare pixel and the bench
          keeps its width; wrapped, each is alone on its line and fills it. */}
      <div style={{ flex: '999 1 460px', minWidth: 0 }}>{stage}</div>
      <div style={{ flex: `1 1 ${benchWidth}px`, minWidth: 0, position: 'sticky', top: 8, alignSelf: 'flex-start' }}>{bench}</div>
    </div>
  )
}

/** Two or three pictures side by side, each giving way evenly, wrapping when there is no room. */
export function StageRow({ children, min = 300 }: { children: ReactNode; min?: number }) {
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 12, marginTop: 10 }}>{children}</div>
}

/** A titled group on the bench. */
export function BenchGroup({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      {title && (
        <div style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-muted)', margin: '0 0 4px' }}>
          {title}
        </div>
      )}
      {children}
    </div>
  )
}

const TONE: Record<string, string> = { good: STATUS.good, warning: STATUS.warning, critical: STATUS.critical }

/**
 * Readouts as rows rather than tiles: label on the left, value on the right,
 * the explanatory note one line under it in small type. Six of them take the
 * height two tiles did, and they sit beside the knob that moves them.
 */
export function BenchStats({ tiles }: { tiles: Readout[] }) {
  return (
    <div style={{ border: '1px solid var(--hair)', borderRadius: 3, background: 'var(--surface-raised)' }}>
      {tiles.map((r, i) => (
        <div key={`${r.label}-${i}`} style={{ padding: '5px 9px', borderTop: i ? '1px solid var(--hair)' : undefined }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--ink-2)' }}>{r.label}</span>
            <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', color: (r.state && TONE[r.state]) || 'var(--ink)' }}>{r.value}</span>
          </div>
          {r.note && <div style={{ fontSize: 10, lineHeight: 1.35, color: 'var(--ink-muted)', marginTop: 1 }}>{r.note}</div>}
        </div>
      ))}
    </div>
  )
}

/** Prose folded away under a one-line summary, open on request. */
export function About({ summary, children, open = false }: { summary: string; children: ReactNode; open?: boolean }) {
  return (
    <details open={open} style={{ margin: '4px 0 8px' }}>
      <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--ink-2)', userSelect: 'none' }}>{summary}</summary>
      <div style={{ fontSize: 11, color: 'var(--ink-muted)', lineHeight: 1.5, marginTop: 5, whiteSpace: 'pre-line' }}>{children}</div>
    </details>
  )
}

/**
 * Hand the thread back between chunks of a long main-thread computation.
 *
 * A MessageChannel ping is the fast way to yield - the page paints between
 * chunks, and a background tab, where timers are throttled to one a second,
 * does not stall. But a chain of them STARVES TIMERS: Chrome runs a posted
 * message ahead of a due setTimeout, so for as long as the chain lasts no
 * timeout on the page fires. For a three-second scan nobody noticed; once
 * the CD-SEM linescan grew to twenty seconds, every debounced slider and
 * every settle timer on the panel sat frozen until it finished. So while the
 * tab is visible every fourth hand-back goes through a zero timeout, which
 * lets the timer queue drain; hidden, where a timeout would cost a second,
 * it stays on the channel.
 */
export function yieldingStepper(tick: () => void): { next(): void; close(): void } {
  const channel = new MessageChannel()
  channel.port1.onmessage = tick
  let n = 0
  return {
    next: () => {
      if (++n % 4 === 0 && typeof document !== 'undefined' && !document.hidden) window.setTimeout(tick, 0)
      else channel.port2.postMessage(null)
    },
    close: () => channel.port1.close(),
  }
}

/**
 * A question mark that holds the paragraph, so the screen holds the number.
 *
 * A readout line keeps what a student glances at - the value, the verdict -
 * and the sentences that explain how it was computed wait behind the mark:
 * hover, focus or tap. The mark is a real button, so it is reachable from
 * the keyboard and a touch screen, and the tip stays open while the pointer
 * is over it, so its text can be selected.
 */
export function HelpTip({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <Tooltip
      title={<div style={{ fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{children}</div>}
      trigger={['hover', 'focus', 'click']}
      placement="topRight"
      styles={{ root: { maxWidth: 420 } }}
    >
      <button
        type="button"
        aria-label={label ?? tOr('tool.panel.HelpMore', 'More about this')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 15,
          height: 15,
          marginLeft: 6,
          padding: 0,
          verticalAlign: 'middle',
          borderRadius: '50%',
          border: '1px solid var(--hair-strong)',
          background: 'transparent',
          color: 'var(--ink-2)',
          font: '600 10px system-ui, sans-serif',
          cursor: 'help',
        }}
      >
        ?
      </button>
    </Tooltip>
  )
}
